import React, { useMemo, useState } from 'react';
import { Search, FolderPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUserLibraryCategories } from '@/hooks/universal-storage/use-library-categories';
import { useUniversalLibrary } from '@/contexts/UniversalLibraryContext';
import { useUniversalLibraryCounts } from '@/hooks/universal-storage/useUniversalLibraryData';
import { Separator } from '@/components/ui/separator';
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import SidebarHeader from '@/components/ui/SidebarHeader';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import FoldersDropdown from './FoldersDropdown';
import ContentTypeDropdown from './ContentTypeDropdown';
import ImportContentDropdown from './ImportContentDropdown';
import StorageContentImporter from './StorageContentImporter';
import { useBulkStorageImageImport, useBulkStorageResearchImport, useBulkStoragePageImport } from '@/hooks/universal-storage/useBulkStorageImport';
import { FileText, Image } from 'lucide-react';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';

const UniversalLibrarySidebar = () => {
  const { isMobile } = useIsMobile();
  const { openCategoryModal } = useGlobalModals();
  const {
    selectedContentType,
    setSelectedContentType,
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    contentTypes,
    triggerRefetch,
  } = useUniversalLibrary();

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importContentType, setImportContentType] = useState<'images' | 'research' | 'pages'>('images');

  // Bulk import hooks
  const bulkImageImport = useBulkStorageImageImport();
  const bulkResearchImport = useBulkStorageResearchImport();
  const bulkPageImport = useBulkStoragePageImport();

  const { data: categories, isLoading: isLoadingCategories } = useUserLibraryCategories();
  const { counts, isLoading: isLoadingCounts } = useUniversalLibraryCounts();

  // Convert registered content types to display format with proper icons and colors
  const availableContentTypes = React.useMemo(() => {
    const typeConfigs = [
      // 🚫 TEMPORARILY REMOVED: Public systems not currently in use
      // To re-enable public content types, uncomment the sections below:
      //
      // {
      //   type: 'research',
      //   name: 'Research',
      //   icon: FileText,
      //   variant: 'green' as const,
      //   iconColor: 'text-green-500',
      //   count: counts?.research,
      // },
      // {
      //   type: 'image',
      //   name: 'Images',
      //   icon: Image,
      //   variant: 'blue' as const,
      //   iconColor: 'text-blue-500',
      //   count: counts?.image,
      // },
      
      // 📝 UPDATED: Renamed and recolored private content types
      {
        type: 'private_image',
        name: 'Image',
        icon: Image,
        variant: 'blue' as const,
        iconColor: 'text-blue-500',
        count: counts?.private_image,
      },
      {
        type: 'private_research',
        name: 'Research',
        icon: FileText,
        variant: 'green' as const,
        iconColor: 'text-green-500',
        count: counts?.private_research,
      },
      // 🎯 NEW CONTENT TYPES CAN BE ADDED HERE
      // Example:
      // {
      //   type: 'new_type',
      //   name: 'New Content',
      //   icon: NewIcon,
      //   variant: 'orange' as const,
      //   iconColor: 'text-orange-500',
      //   count: counts?.new_type,
      // },
    ];

    // Only return types that are actually registered
    return typeConfigs.filter(config => contentTypes[config.type]);
  }, [contentTypes, counts]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleContentTypeChange = (type: string) => {
    setSelectedContentType(type);
  };

  const handleCategoryChange = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
  };

  // Handle import content type selection
  const handleImportContentTypeSelect = (contentType: 'images' | 'research' | 'pages') => {
    setImportContentType(contentType);
    setImportModalOpen(true);
  };

  // Handle bulk import with proper cache invalidation
  const handleBulkImport = async (selectedIds: string[], categoryId?: string) => {
    const importHook = importContentType === 'images' ? bulkImageImport :
                      importContentType === 'research' ? bulkResearchImport :
                      bulkPageImport;

    try {
      await importHook.mutateAsync({ itemIds: selectedIds, categoryId });
      
      // CRITICAL: Trigger Universal Library refetch explicitly
      // This ensures the UI updates immediately after import
      console.log('[UniversalLibrarySidebar] Triggering explicit refetch after bulk import');
      triggerRefetch();
    } catch (error) {
      console.error('[UniversalLibrarySidebar] Bulk import failed:', error);
      // Error is already handled by the mutation hooks
    }
  };

  // Memoize the sidebar content to prevent re-initialization during re-renders
  const sidebarContent = useMemo(() => (
    <div className="space-y-3 pt-2">
      {/* Import Content Dropdown */}
      <ImportContentDropdown
        onContentTypeSelect={handleImportContentTypeSelect}
      />

      {/* Divider after Import Content */}
      <Separator className="bg-accent/20" />

      {/* Search Field */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20">
          <Search size={16} className="text-accent" />
        </div>
        <Input 
          placeholder="Search storage..." 
          value={searchQuery}
          onChange={handleSearchChange}
          className="pl-10 h-10 text-sm font-medium bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 hover:border-accent/30"
        />
      </div>

      {/* Content Type Dropdown */}
      <ContentTypeDropdown
        selectedContentType={selectedContentType}
        onContentTypeChange={handleContentTypeChange}
        availableContentTypes={availableContentTypes}
        totalCount={counts?.total}
        isLoadingCounts={isLoadingCounts}
      />

      {/* Divider before Folders section */}
      <Separator className="bg-accent/20" />

      {/* Folders Dropdown */}
      <FoldersDropdown
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        categories={categories}
        counts={counts}
        isLoadingCategories={isLoadingCategories}
        isLoadingCounts={isLoadingCounts}
      />

      {/* Create Folder Button */}
      <Button
        onClick={openCategoryModal}
        variant="default"
        className="w-full justify-center h-10 bg-gradient-to-r from-[var(--brand-gradient-from)] to-[var(--brand-gradient-to)] hover:opacity-90 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200"
      >
        <FolderPlus size={16} className="mr-2" />
        <span>Create Folder</span>
      </Button>
    </div>
  ), [
    searchQuery,
    handleSearchChange,
    selectedCategory,
    handleCategoryChange,
    categories,
    counts,
    isLoadingCategories,
    isLoadingCounts,
    selectedContentType,
    handleContentTypeChange,
    availableContentTypes,
    openCategoryModal,
  ]);

  return (
    <div className="h-full">
      <MobileSidebarAccordion
        variant="purple"
        persistKey="universal-library-sidebar"
        defaultExpanded={false}
        className="h-full"
      >
        <SidebarHeader 
          title="Import & Search" 
          icon={Search} 
          variant="purple"
        />
        {sidebarContent}
      </MobileSidebarAccordion>

      {/* Storage Content Importer Modal */}
      <StorageContentImporter
        contentType={importContentType}
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onBulkImport={handleBulkImport}
        layout={importContentType === 'images' ? 'grid' : 'list'}
      />
    </div>
  );
};

export default UniversalLibrarySidebar;
